<template>
  <div v-if="shipments.length > 0" class="multiple-results-table">
    <div class="multiple-results-table__custom-headers">
      <div class="heading font--small--bold">
        <span class="heading--text">
          {{ $t("track.blLabel") }}
        </span>
      </div>
      <div class="heading font--small--bold">
        <span class="heading--text">
          {{ $t("common.from") }}
        </span>
      </div>
      <div class="heading font--small--bold">
        <span class="heading--text">
          {{ $t("common.to") }}
        </span>
      </div>
      <div class="heading font--small--bold">
        <span class="heading--text">
          {{ $t("common.vessel") }}
        </span>
      </div>
    </div>
    <div
      class="multiple-results-table__table-list font--small"
      role="table"
      aria-label="multiple-results-table list which shows shipments relevant to your booking reference"
      data-test="Default-Table-List"
    >
      <div
        v-for="(shipment, index) in shipments"
        :key="shipment.uniqueKey"
        :class="[
          'multiple-results-table__table-list--row',
          { first: index === 0, last: index === shipments.length - 1 }
        ]"
        role="row"
        tabindex="0"
        @click="openShipment(index)"
        @keypress="openShipment(index)"
      >
        <div class="multiple-results-table__table-list--row-column">
          <div class="heading font--small--bold" role="columnheader">
            <span data-test="Action-ColumnHeader-DefaultTable" class="heading--text">
              {{ $t("track.blLabel") }}
            </span>
          </div>
          <div class="content content--lading font--small--bold" role="cell">
            <div data-test="multiple-bol-number"></div>
            <span class="lading-number">{{ shipment.ladingNumber || "-" }}</span>
          </div>
          <div class="content" role="cell">
            <span class="heading" data-test="BookedByRef-text" :aria-label="bookByReference"
              >{{ $t("common.bookedByRef") }} {{ bookByReference }}</span
            >
          </div>
        </div>

        <div
          :class="[
            'multiple-results-table__table-list--row-column',
            selectedRow === index ? 'column-row--uncollapse' : 'column-row--collapse'
          ]"
        >
          <div class="heading font--small--bold" role="columnheader">
            <span data-test="ColumnHeader-DefaultTable-From" class="heading--text">
              {{ $t("common.from") }}
            </span>
          </div>
          <div class="content" role="cell">
            <span data-test="PlaceOfReciept-Content-Text" :aria-label="shipment.from.placeOfReceipt.siteName">{{
              `${(shipment.from.placeOfReceipt && shipment.from.placeOfReceipt.siteName) || "-"}, ${
                (shipment.from.placeOfReceipt && shipment.from.placeOfReceipt.countryCode) || "-"
              }`
            }}</span>
            <div>{{ dateFormat(shipment.from.etd, "DD MMM") }}</div>
          </div>
          <div class="content content--location" role="cell">
            <span
              class="heading"
              data-test="PortOfLoading-Content-Text"
              :aria-label="shipment.from.portOfLoading.siteName"
              >{{ $t("common.departing") }}
              {{
                `${(shipment.from.portOfLoading && shipment.from.portOfLoading.siteName) || "-"}, ${
                  (shipment.from.portOfLoading && shipment.from.portOfLoading.countryCode) || "-"
                }`
              }}</span
            >
          </div>
        </div>

        <div
          :class="[
            'multiple-results-table__table-list--row-column',
            selectedRow === index ? 'column-row--uncollapse' : 'column-row--collapse'
          ]"
        >
          <div class="heading font--small--bold" role="columnheader">
            <span data-test="To-ColumnHeader-DefaultTable" class="heading--text">
              {{ $t("common.to") }}
            </span>
          </div>
          <div class="content" role="cell">
            <span data-test="PlaceOfDelivery-Content-Text" :aria-label="shipment.to">{{
              ` ${(shipment.to.placeOfDelivery && shipment.to.placeOfDelivery.siteName) || "-"}, ${
                (shipment.to.placeOfDelivery && shipment.to.placeOfDelivery.countryCode) || "-"
              }`
            }}</span>
            <div>
              {{ dateFormat(shipment.to.eta, "DD MMM") }}
            </div>
          </div>
          <div class="content content--location" role="cell">
            <span
              class="heading"
              data-test="PortOfDischarge-Content-Text"
              :aria-label="shipment.to.portOfDischarge.siteName"
              >{{ $t("common.arriving") }}
              {{
                `${(shipment.to.portOfDischarge && shipment.to.portOfDischarge.siteName) || "-"}, ${
                  (shipment.to.portOfDischarge && shipment.to.portOfDischarge.countryCode) || "-"
                }`
              }}
            </span>
          </div>
        </div>

        <div
          :class="[
            'multiple-results-table__table-list--row-column',
            selectedRow === index ? 'column-row--uncollapse' : 'column-row--collapse'
          ]"
        >
          <div class="heading font--small--bold" role="columnheader">
            <span data-test="Vessel-ColumnHeader-DefaultTable" class="heading--text">
              {{ $t("common.vessel") }}
            </span>
          </div>
          <div class="content" role="cell">
            <span data-test="ArrivingVessel-Content-Text" :aria-label="shipment.from">{{
              (shipment.arrivingVessel && shipment.arrivingVessel.name) || "-"
            }}</span>
          </div>
          <div class="content content--location" role="cell">
            <span class="heading" data-test="ArrivingVessel-Content-Text" :aria-label="shipment.arrivingVessel.flagCode"
              >{{ $t("vessel.flag") + ": " }}
              {{
                `${(shipment.arrivingVessel && shipment.arrivingVessel.flagCode) || "-"}` +
                ", " +
                $t("vessel.flag") +
                ": " +
                ((shipment.arrivingVessel && shipment.arrivingVessel.buildYear) || "-")
              }}
            </span>
          </div>
        </div>
        <mc-button
          :class="['accordian-toggle', { first: index === 0 }, selectedRow === index ? 'collaspe' : 'uncollaspe']"
          data-test="ShowMoreDetails-Accordion-Button"
          variant="plain"
          appearance="primary"
          :icon="selectedRow === index ? 'chevron-up' : 'chevron-down'"
          @click="showDetails(index, $event)"
        />

        <div class="seperator"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { dateFormat } from "@/lib/filters";

export default {
  name: "MultipleResultsTable",

  props: {
    shipments: {
      type: Array,
      required: true
    },
    bookByReference: {
      type: String,
      default: "-"
    }
  },

  data() {
    return {
      selectedRow: null
    };
  },
  methods: {
    dateFormat,
    showDetails(index, e) {
      if (this.selectedRow === index) {
        this.selectedRow = null;
        e.target.closest("button").setAttribute("aria-expanded", false);
      } else {
        this.selectedRow = index;
        e.target.closest("button").setAttribute("aria-expanded", true);
      }
    },
    openShipment(index) {
      window.location.href = `/shipment-details/${this.shipments[index].ladingNumber}`;
    }
  }
};
</script>
